// Vuex module for the conversation internal counsel side panel.
//
// Everything is keyed by conversation display id (the same id the ActionCable
// payloads carry), because an agent can jump between conversations while turns
// for the previous one are still running upstream.
//
// Panel visibility is deliberately NOT stored here: it lives in the user's
// `is_counsel_panel_open` uiSetting, alongside the contact and copilot panels,
// so the three stay mutually exclusive and survive a reload.
//
// namespaced:false matches the ai_assist / translation modules, so getters and
// actions are reached as store.getters.xxx; names are prefixed `counsel*`.
import {
  fetchCounsel,
  createCounselMessage,
  deleteCounselMessage,
  retryCounselTurn,
  abortCounselTurn,
  clearCounselThread as clearCounselThreadRequest,
  updateCounselAutoReply,
} from '../api';
import { loadMode, saveMode, DEFAULT_MODE, MODES } from '../preferences';
import logger from '../logger';

const ROLE_ASSISTANT = 'assistant';
// System notices that close a turn. Everything else a turn writes (tool results,
// a customer reply echo, a shared KB image) happens mid-flight, so the thinking
// indicator must survive them.
const TURN_END_KINDS = ['turn_failed', 'turn_aborted'];

const endsTurn = message => {
  const kind = message?.metadata?.kind;
  if (message?.role === ROLE_ASSISTANT) return kind !== 'kb_image';
  return TURN_END_KINDS.includes(kind);
};

const resolveAccountId = rootState => {
  const fromRoute = rootState?.route?.params?.accountId;
  if (fromRoute) return fromRoute;
  const fromAccounts = rootState?.accounts?.currentAccountId;
  if (fromAccounts) return fromAccounts;
  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(/\/accounts\/(\d+)/);
    if (match) return match[1];
  }
  return null;
};

const resolveUserId = rootState => rootState?.auth?.currentUser?.id || null;

const key = conversationId => String(conversationId);

// Match Conversations::AgentActivity::TTL_SECONDS so a missed clear event
// cannot leave the banner spinning. Heartbeats reset this timer.
const AGENT_ACTIVITY_TIMEOUT_MS = 180_000;
const activityTimers = {};

const armActivityTimeout = (commit, conversationId) => {
  const id = key(conversationId);
  if (activityTimers[id]) clearTimeout(activityTimers[id]);
  activityTimers[id] = setTimeout(() => {
    commit('SET_AGENT_ACTIVITY', { conversationId, busy: false });
    logger.warn('agentActivity.timeout', { conversationId });
    delete activityTimers[id];
  }, AGENT_ACTIVITY_TIMEOUT_MS);
};

const clearActivityTimeout = conversationId => {
  const id = key(conversationId);
  if (!activityTimers[id]) return;
  clearTimeout(activityTimers[id]);
  delete activityTimers[id];
};

const counselBusyTimers = {};

const armCounselBusyTimeout = (commit, getters, conversationId, requestId) => {
  const id = key(conversationId);
  if (counselBusyTimers[id]) clearTimeout(counselBusyTimers[id]);
  counselBusyTimers[id] = setTimeout(() => {
    applyCounselBusy(commit, getters, conversationId, {
      busy: false,
      requestId,
    });
    logger.warn('counselBusy.timeout', { conversationId });
    delete counselBusyTimers[id];
  }, AGENT_ACTIVITY_TIMEOUT_MS);
};

const clearCounselBusyTimeout = conversationId => {
  const id = key(conversationId);
  if (!counselBusyTimers[id]) return;
  clearTimeout(counselBusyTimers[id]);
  delete counselBusyTimers[id];
};

const EMPTY_FLAGS = Object.freeze({
  fetching: false,
  loaded: false,
  sending: false,
  // A human line was accepted; the assistant/system answer is still upstream.
  awaitingReply: false,
  updatingAutoReply: false,
  retrying: false,
  clearing: false,
  // Turn id of the running turn — what Stop needs to address.
  requestId: null,
  // Agent who claimed the shared counsel lock; observers must not Stop.
  actorUserId: null,
  aborting: false,
});

const applyCounselBusy = (
  commit,
  getters,
  conversationId,
  { busy, userId, requestId }
) => {
  const local = getters.counselUiFlagsFor(conversationId);
  if (!busy) {
    // A newer local send (or a later claim) must not be wiped by a stale clear.
    if (local.sending) return;
    if (
      local.requestId &&
      requestId &&
      String(local.requestId) !== String(requestId)
    ) {
      return;
    }
    clearCounselBusyTimeout(conversationId);
    commit('SET_COUNSEL_FLAGS', {
      conversationId,
      flags: {
        awaitingReply: false,
        requestId: null,
        actorUserId: null,
        aborting: false,
      },
    });
    return;
  }
  commit('SET_COUNSEL_FLAGS', {
    conversationId,
    flags: {
      awaitingReply: true,
      requestId: requestId || local.requestId || null,
      actorUserId: userId || local.actorUserId || null,
    },
  });
  armCounselBusyTimeout(
    commit,
    getters,
    conversationId,
    requestId || local.requestId || null
  );
};

// The API returns messages in chronological order and broadcasts arrive in the
// order they were written, but a slow GET can land after a live event — sort so
// the list never depends on arrival order.
const sortMessages = messages =>
  [...messages].sort((a, b) => a.created_at - b.created_at || a.id - b.id);

const initialState = () => ({
  mode: DEFAULT_MODE,
  threads: {}, // conversationId -> { id, created_at }
  messages: {}, // conversationId -> [message]
  autoReply: {}, // conversationId -> boolean
  autoReplyBusy: {}, // conversationId -> boolean (shared auto-reply activity)
  botBound: {}, // conversationId -> boolean
  uiFlags: {}, // conversationId -> EMPTY_FLAGS shape
});

const moduleGetters = {
  counselMode: state => state.mode,
  counselThreadFor: state => conversationId =>
    state.threads[key(conversationId)] || null,
  counselMessagesFor: state => conversationId =>
    state.messages[key(conversationId)] || [],
  // Until fetchCounselThread runs, missing keys are treated as "on" for UI
  // badges; ReplyBox only blocks after flags.loaded so legacy open chats are
  // not locked before the host value (pending=on / other=off when no row) arrives.
  counselAutoReplyFor: state => conversationId =>
    state.autoReply[key(conversationId)] !== false,
  counselAutoReplyBusyFor: state => conversationId =>
    state.autoReplyBusy[key(conversationId)] === true,
  // Assume bound until the first response says otherwise, so the "bind a bot"
  // hint never flashes while the panel is still loading.
  counselBotBoundFor: state => conversationId =>
    state.botBound[key(conversationId)] !== false,
  counselUiFlagsFor: state => conversationId =>
    state.uiFlags[key(conversationId)] || { ...EMPTY_FLAGS },
};

const mutations = {
  SET_COUNSEL_MODE(state, mode) {
    state.mode = mode;
  },
  SET_COUNSEL_FLAGS(state, { conversationId, flags }) {
    const id = key(conversationId);
    state.uiFlags = {
      ...state.uiFlags,
      [id]: { ...(state.uiFlags[id] || EMPTY_FLAGS), ...flags },
    };
  },
  SET_COUNSEL_THREAD(state, { conversationId, thread }) {
    const id = key(conversationId);
    state.threads = {
      ...state.threads,
      [id]: thread ? { id: thread.id, created_at: thread.created_at } : null,
    };
    state.messages = {
      ...state.messages,
      [id]: sortMessages(thread?.messages || []),
    };
  },
  // The first message of a conversation creates the thread; record it without
  // touching the message list, which live events may already have moved on.
  SET_COUNSEL_THREAD_META(state, { conversationId, thread }) {
    if (!thread) return;
    const id = key(conversationId);
    state.threads = {
      ...state.threads,
      [id]: { id: thread.id, created_at: thread.created_at },
    };
  },
  UPSERT_COUNSEL_MESSAGE(state, { conversationId, message }) {
    const id = key(conversationId);
    const current = state.messages[id] || [];
    // The POST response and the broadcast carry the same human message.
    const without = current.filter(item => item.id !== message.id);
    state.messages = {
      ...state.messages,
      [id]: sortMessages([...without, message]),
    };
  },
  REMOVE_COUNSEL_MESSAGE(state, { conversationId, messageId }) {
    const id = key(conversationId);
    const current = state.messages[id];
    if (!current) return;
    state.messages = {
      ...state.messages,
      [id]: current.filter(item => item.id !== messageId),
    };
  },
  CLEAR_COUNSEL_MESSAGES(state, { conversationId }) {
    const id = key(conversationId);
    state.messages = { ...state.messages, [id]: [] };
  },
  SET_COUNSEL_AUTO_REPLY(state, { conversationId, enabled }) {
    state.autoReply = { ...state.autoReply, [key(conversationId)]: enabled };
  },
  SET_AGENT_ACTIVITY(state, { conversationId, busy }) {
    state.autoReplyBusy = {
      ...state.autoReplyBusy,
      [key(conversationId)]: !!busy,
    };
  },
  SET_COUNSEL_BOT_BOUND(state, { conversationId, bound }) {
    state.botBound = { ...state.botBound, [key(conversationId)]: bound };
  },
};

const actions = {
  loadCounselMode({ commit, rootState }) {
    const mode = loadMode(resolveUserId(rootState));
    commit('SET_COUNSEL_MODE', mode);
    return mode;
  },

  setCounselMode({ commit, rootState }, mode) {
    if (!MODES.includes(mode)) return;
    saveMode(resolveUserId(rootState), mode);
    commit('SET_COUNSEL_MODE', mode);
    logger.info('mode.changed', { mode });
  },

  // Both the panel and the header status control call this; the fetching flag
  // keeps them from issuing the same GET twice.
  async fetchCounselThread({ commit, getters, rootState }, conversationId) {
    if (!conversationId) return;
    if (getters.counselUiFlagsFor(conversationId).fetching) return;

    const accountId = resolveAccountId(rootState);
    if (!accountId) return;

    commit('SET_COUNSEL_FLAGS', { conversationId, flags: { fetching: true } });
    try {
      const data = await fetchCounsel({ accountId, conversationId });
      commit('SET_COUNSEL_THREAD', { conversationId, thread: data.thread });
      commit('SET_COUNSEL_AUTO_REPLY', {
        conversationId,
        enabled: data.auto_reply_enabled !== false,
      });
      commit('SET_COUNSEL_BOT_BOUND', {
        conversationId,
        bound: !!data.bot_bound,
      });
      commit('SET_AGENT_ACTIVITY', {
        conversationId,
        busy: !!data.auto_reply_busy && data.auto_reply_enabled !== false,
      });
      if (data.auto_reply_busy && data.auto_reply_enabled !== false) {
        armActivityTimeout(commit, conversationId);
      } else {
        clearActivityTimeout(conversationId);
      }
      applyCounselBusy(commit, getters, conversationId, {
        busy: !!data.counsel_busy,
        userId: data.counsel_user_id,
        requestId: data.counsel_request_id,
      });
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: { fetching: false, loaded: true },
      });
    } catch (err) {
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: { fetching: false },
      });
      logger.error('thread.fetchFailed', { conversationId });
      throw err;
    }
  },

  async sendCounselMessage(
    { commit, getters, rootState },
    { conversationId, content, replyToMessageId }
  ) {
    const accountId = resolveAccountId(rootState);
    if (!accountId || !content) return;

    const mode = getters.counselMode;
    const me = resolveUserId(rootState);
    const current = getters.counselUiFlagsFor(conversationId);
    if (
      current.awaitingReply &&
      current.actorUserId &&
      Number(current.actorUserId) !== Number(me)
    ) {
      const err = new Error('counsel_turn_in_progress');
      err.response = { status: 409 };
      throw err;
    }
    commit('SET_COUNSEL_FLAGS', {
      conversationId,
      flags: { sending: true, actorUserId: me },
    });
    try {
      const data = await createCounselMessage({
        accountId,
        conversationId,
        content,
        mode,
        replyToMessageId,
      });
      commit('SET_COUNSEL_THREAD_META', {
        conversationId,
        thread: data.thread,
      });
      commit('UPSERT_COUNSEL_MESSAGE', {
        conversationId,
        message: data.message,
      });
      // Stop pressed while this POST was in flight: the panel already unlocked,
      // but the turn is now queued upstream — send the flag with the id we just
      // learned so the turn stops instead of running dry.
      const stoppedEarly = getters.counselUiFlagsFor(conversationId).aborting;
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: {
          sending: false,
          awaitingReply: !stoppedEarly,
          loaded: true,
          requestId: stoppedEarly ? null : data.request_id || null,
          actorUserId: stoppedEarly ? null : me,
          aborting: false,
        },
      });
      if (stoppedEarly && data.request_id) {
        abortCounselTurn({
          accountId,
          conversationId,
          requestId: data.request_id,
        }).catch(() => {});
      }
      logger.info('message.sent', { conversationId, mode });
    } catch (err) {
      const conflict = err?.response?.status === 409;
      const payload = err?.response?.data || {};
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: { sending: false },
      });
      if (conflict) {
        applyCounselBusy(commit, getters, conversationId, {
          busy: true,
          userId: payload.counsel_user_id,
          requestId: payload.counsel_request_id,
        });
      }
      logger.error('message.sendFailed', { conversationId, mode });
      throw err;
    }
  },

  // Agent-initiated prune of a human/assistant line. Optimistic remove; the
  // ActionCable event is a no-op if the row is already gone.
  async clearCounselThread({ commit, getters, rootState }, { conversationId }) {
    const accountId = resolveAccountId(rootState);
    if (!accountId) return;
    if (getters.counselUiFlagsFor(conversationId).clearing) return;
    if (getters.counselUiFlagsFor(conversationId).awaitingReply) {
      const err = new Error('counsel_turn_in_progress');
      err.response = { status: 409 };
      throw err;
    }

    commit('SET_COUNSEL_FLAGS', { conversationId, flags: { clearing: true } });
    try {
      await clearCounselThreadRequest({ accountId, conversationId });
      commit('CLEAR_COUNSEL_MESSAGES', { conversationId });
      commit('SET_COUNSEL_FLAGS', { conversationId, flags: { clearing: false } });
      logger.info('thread.cleared', { conversationId });
    } catch (err) {
      commit('SET_COUNSEL_FLAGS', { conversationId, flags: { clearing: false } });
      logger.error('thread.clearFailed', { conversationId });
      throw err;
    }
  },

  async deleteCounselMessage(
    { commit, rootState },
    { conversationId, messageId }
  ) {
    const accountId = resolveAccountId(rootState);
    if (!accountId || !messageId) return;

    commit('REMOVE_COUNSEL_MESSAGE', { conversationId, messageId });
    try {
      await deleteCounselMessage({ accountId, conversationId, messageId });
      logger.info('message.deleted', { conversationId, messageId });
    } catch (err) {
      // Reload so a failed delete does not leave the panel out of sync.
      logger.error('message.deleteFailed', { conversationId, messageId });
      throw err;
    }
  },

  // Re-runs the last question after a failed turn; nothing is re-typed and no
  // new line enters the thread, so the retried turn sees the original context.
  async retryCounselTurn({ commit, getters, rootState }, { conversationId }) {
    const accountId = resolveAccountId(rootState);
    if (!accountId) return;
    const current = getters.counselUiFlagsFor(conversationId);
    if (current.retrying || current.awaitingReply) return;

    commit('SET_COUNSEL_FLAGS', { conversationId, flags: { retrying: true } });
    try {
      const data = await retryCounselTurn({ accountId, conversationId });
      if (data?.removed_message_id) {
        commit('REMOVE_COUNSEL_MESSAGE', {
          conversationId,
          messageId: data.removed_message_id,
        });
      }
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: {
          retrying: false,
          awaitingReply: true,
          requestId: data?.request_id || null,
          actorUserId: resolveUserId(rootState),
          aborting: false,
        },
      });
      logger.info('turn.retried', { conversationId });
    } catch (err) {
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: { retrying: false },
      });
      logger.error('turn.retryFailed', { conversationId });
      throw err;
    }
  },

  // Agent pressed Stop on a running turn. The panel unlocks immediately (the
  // agent asked to move on) while the host is told best-effort; the thread later
  // shows the "stopped" notice written by the job that observed the flag.
  async abortCounselTurn({ commit, getters, rootState }, { conversationId }) {
    const accountId = resolveAccountId(rootState);
    const { requestId, sending, actorUserId } = getters.counselUiFlagsFor(
      conversationId
    );
    const me = resolveUserId(rootState);
    if (actorUserId && Number(actorUserId) !== Number(me)) {
      logger.warn('turn.abortDenied', { conversationId, actorUserId });
      return;
    }

    clearCounselBusyTimeout(conversationId);
    commit('SET_COUNSEL_FLAGS', {
      conversationId,
      flags: {
        awaitingReply: false,
        requestId: null,
        actorUserId: null,
        // The id is still on its way back; sendCounselMessage forwards the stop.
        aborting: !requestId && sending,
      },
    });
    logger.info('turn.abort', { conversationId, requestId });
    if (!accountId || !requestId) return;

    try {
      await abortCounselTurn({ accountId, conversationId, requestId });
    } catch (err) {
      logger.warn('turn.abortFailed', { conversationId, requestId });
    }
  },

  async toggleCounselAutoReply(
    { commit, rootState },
    { conversationId, enabled }
  ) {
    const accountId = resolveAccountId(rootState);
    if (!accountId) return;

    commit('SET_COUNSEL_FLAGS', {
      conversationId,
      flags: { updatingAutoReply: true },
    });
    try {
      const data = await updateCounselAutoReply({
        accountId,
        conversationId,
        enabled,
      });
      commit('SET_COUNSEL_AUTO_REPLY', {
        conversationId,
        enabled: data.auto_reply_enabled !== false,
      });
      if (data.auto_reply_enabled === false) {
        commit('SET_AGENT_ACTIVITY', { conversationId, busy: false });
        clearActivityTimeout(conversationId);
      }
    } finally {
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: { updatingAutoReply: false },
      });
    }
  },

  // ActionCable: counsel.message_created
  counselMessageReceived({ commit }, { conversationId, message }) {
    if (!conversationId || !message) return;
    commit('UPSERT_COUNSEL_MESSAGE', { conversationId, message });
    if (endsTurn(message)) {
      commit('SET_COUNSEL_FLAGS', {
        conversationId,
        flags: {
          awaitingReply: false,
          requestId: null,
          actorUserId: null,
          aborting: false,
        },
      });
    }
    logger.info('message.received', {
      conversationId,
      messageId: message.id,
      role: message.role,
    });
  },

  // ActionCable: counsel.message_deleted — prune (agent delete) or a replayed
  // failure notice. Do not touch awaitingReply here; retry sets it explicitly.
  counselMessageDeleted({ commit }, { conversationId, messageId }) {
    if (!conversationId || !messageId) return;
    commit('REMOVE_COUNSEL_MESSAGE', { conversationId, messageId });
    logger.info('message.deleted', { conversationId, messageId });
  },

  // ActionCable: counsel.thread_cleared
  counselThreadCleared({ commit }, { conversationId }) {
    if (!conversationId) return;
    commit('CLEAR_COUNSEL_MESSAGES', { conversationId });
    logger.info('thread.clearedRemote', { conversationId });
  },

  // ActionCable: counsel.auto_reply_changed
  counselAutoReplyReceived({ commit }, { conversationId, enabled }) {
    if (!conversationId) return;
    commit('SET_COUNSEL_AUTO_REPLY', { conversationId, enabled });
    if (enabled === false) {
      commit('SET_AGENT_ACTIVITY', { conversationId, busy: false });
      clearActivityTimeout(conversationId);
    }
    logger.info('autoReply.received', { conversationId, enabled });
  },

  // ActionCable: conversation.agent_activity (auto-reply or counsel)
  counselAgentActivityReceived(
    { commit, getters },
    { conversationId, busy, kind, userId, requestId }
  ) {
    if (!conversationId) return;
    if (kind === 'counsel') {
      applyCounselBusy(commit, getters, conversationId, {
        busy: !!busy,
        userId,
        requestId,
      });
      logger.info('agentActivity.received', {
        conversationId,
        busy,
        kind,
        userId,
      });
      return;
    }
    if (kind && kind !== 'auto_reply') return;
    commit('SET_AGENT_ACTIVITY', { conversationId, busy: !!busy });
    if (busy) {
      armActivityTimeout(commit, conversationId);
    } else {
      clearActivityTimeout(conversationId);
    }
    logger.info('agentActivity.received', { conversationId, busy, kind });
  },
};

export default {
  namespaced: false,
  state: initialState,
  getters: moduleGetters,
  mutations,
  actions,
};
